import { useTranslation } from "react-i18next"
import {
  BetterDocTable,
  BetterDocTableCell,
  BetterDocTableHeaderCell,
} from "@/modules/admin/better-doc/components/better-doc-table"
import { EMPTY_VALUE_PLACEHOLDER } from "@/modules/admin/better-doc/constants/better-doc-constants"
import { formatBetterDocValue, isBetterDocTextPresent } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import { BetterDocRemoteMedic } from "@/modules/admin/better-doc/services/betterDocRemoteRecommendationQuery"

type BetterDocRemoteMedicTableProps = {
  medics: ReadonlyArray<BetterDocRemoteMedic>
}

type BetterDocRemoteMedicColumn = {
  key: string
  header: string
  cell: (medic: BetterDocRemoteMedic) => React.ReactNode
}

const formatSelection = (selectionType: string | null, selectionId: number | null) =>
  [selectionType, selectionId].filter((part) => part !== null && part !== "").join(" ") || EMPTY_VALUE_PLACEHOLDER

export const BetterDocRemoteMedicTable = ({ medics }: BetterDocRemoteMedicTableProps) => {
  const { t } = useTranslation()

  const columns: ReadonlyArray<BetterDocRemoteMedicColumn> = [
    {
      key: "name",
      header: t("admin.betterDoc.remoteMedicTable.name"),
      cell: (medic) => formatBetterDocValue(medic.name),
    },
    {
      key: "gender",
      header: t("admin.betterDoc.remoteMedicTable.gender"),
      cell: (medic) => formatBetterDocValue(medic.gender),
    },
    {
      key: "workplace",
      header: t("admin.betterDoc.remoteMedicTable.workplace"),
      cell: ({ workplace }) =>
        workplace === null ? (
          EMPTY_VALUE_PLACEHOLDER
        ) : (
          <div className="flex flex-col">
            <span>{formatBetterDocValue(workplace.name)}</span>
            {isBetterDocTextPresent(workplace.ancestorName) ? (
              <span className="text-on-surface-subdued text-xs">{workplace.ancestorName}</span>
            ) : null}
          </div>
        ),
    },
    {
      key: "address",
      header: t("admin.betterDoc.remoteMedicTable.address"),
      cell: ({ workplace }) => {
        if (workplace === null) {
          return EMPTY_VALUE_PLACEHOLDER
        }

        const addressLines = [
          workplace.address,
          [workplace.postalCode, workplace.city].filter(isBetterDocTextPresent).join(" "),
          workplace.countryCode,
        ].filter(isBetterDocTextPresent)

        return addressLines.length === 0 ? (
          EMPTY_VALUE_PLACEHOLDER
        ) : (
          <div className="flex flex-col">
            {addressLines.map((addressLine) => (
              <span key={addressLine}>{addressLine}</span>
            ))}
          </div>
        )
      },
    },
    {
      key: "travelTime",
      header: t("admin.betterDoc.remoteMedicTable.travelTime"),
      cell: (medic) =>
        medic.travelTimeCar === null
          ? EMPTY_VALUE_PLACEHOLDER
          : t("admin.betterDoc.remoteMedicTable.minutes", { minutes: medic.travelTimeCar }),
    },
    {
      key: "glnNumber",
      header: t("admin.betterDoc.remoteMedicTable.glnNumber"),
      cell: (medic) => formatBetterDocValue(medic.glnNumber),
    },
    {
      key: "zsrNumber",
      header: t("admin.betterDoc.remoteMedicTable.zsrNumber"),
      cell: (medic) => formatBetterDocValue(medic.zsrNumber),
    },
    {
      key: "selection",
      header: t("admin.betterDoc.remoteMedicTable.selection"),
      cell: (medic) => formatSelection(medic.selectionType, medic.selectionId),
    },
  ]

  if (medics.length === 0) {
    return (
      <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-remote-medic-table-empty">
        {t("admin.betterDoc.remoteMedicTable.empty")}
      </p>
    )
  }

  return (
    <BetterDocTable data-testid="better-doc-admin-remote-medic-table">
      <thead>
        <tr>
          {columns.map((column) => (
            <BetterDocTableHeaderCell key={column.key}>{column.header}</BetterDocTableHeaderCell>
          ))}
        </tr>
      </thead>
      <tbody>
        {medics.map((medic) => (
          <tr
            key={`${medic.selectionType}-${medic.selectionId}-${medic.zsrNumber}-${medic.name}`}
            className="hover:bg-surface-hovered"
            data-testid="better-doc-admin-remote-medic-row">
            {columns.map((column) => (
              <BetterDocTableCell key={column.key}>{column.cell(medic)}</BetterDocTableCell>
            ))}
          </tr>
        ))}
      </tbody>
    </BetterDocTable>
  )
}
